import React, { useEffect, useState } from 'react';
import Zana, { ZanaState } from './Zana';
import { isBriefingsConfigured, fetchZanaBriefing, runZanaFleetRead, briefingAge, asList } from '../services/briefingsApi';
import { intentsFromInsightText, ZanaIntentExecutor } from '../services/zanaActions';
import ZanaActionChips from './ZanaActionChips';
import { customToast } from '../utils/customToast';

/* ============================================================================
   ZanaSummaryCard — Zana's at-a-glance Fleet Health summary on the main
   Dashboard tab. Reads aiBriefings/{orgCode} (written by the Render backend).

   • Premium/Enterprise (zanaEnabled): the live summary — health *status* (word
     + Zana's face), headline and top action, with Action Registry chips for
     "Do next" and "See full analysis". The raw 0–100 score is kept off-screen:
     owners read "Clean / Steady / Needs attention", and her animation tracks
     that band. Until a briefing exists she sits in configuring / learning
     (never blank).
   • Basic/Pilot: a compact "wake Zana" upsell strip.
   ========================================================================== */

interface Briefing {
  source?: 'ai' | 'deterministic' | 'learning' | 'configuring';
  configureStage?: string;
  configureStageIndex?: number;
  configureTotalStages?: number;
  mascotState?: ZanaState;
  headline?: string;
  summary?: string;
  healthScore?: number | null;
  learningDaysLeft?: number;
  learningProgress?: number;
  learningGaps?: string[];
  learnedStats?: {
    // Inventory — known as soon as the owner adds them.
    buses?: number; children?: number; drivers?: number; parents?: number;
    // Intelligence — accrues as the fleet runs.
    busesWithTelemetry?: number; routes?: number; learnedRoutes?: number; trips?: number; ratedDrivers?: number;
  };
  recommendations?: Array<{ text: string }>;
  missions?: Array<{
    id: string; pillar: string; title: string; why: string; nextStep: string;
    actionId?: string; params?: Record<string, unknown>;
  }>;
  /* When the briefing was written. Read so this card can admit it is showing
     an old one — it renders the same document as the Zana tab, and was just as
     capable of presenting four-week-old numbers as current. */
  generatedAtMs?: number;
}

const HERO_BG = 'radial-gradient(120% 130% at 88% -10%, #2a1c4d 0%, #191233 55%, #120d22 100%)';

/** Map the internal 0–100 score to a plain-English band + matching face.
 *  Bands match the briefing headlines (≥85 clean, ≥70 steady, else attention).
 *  The number itself is never shown — it read as a todo count. */
function healthBand(score: number | null | undefined): { label: string; face: ZanaState } | null {
  if (score == null || !Number.isFinite(score)) return null;
  if (score >= 85) return { label: 'Clean', face: 'happy' };
  if (score >= 70) return { label: 'Steady', face: 'online' };
  return { label: 'Needs attention', face: 'concerned' };
}

export default function ZanaSummaryCard({
  orgCode, zanaEnabled, onOpenAnalytics, onUpgrade, onExecuteIntent, liveUnreadAlertCount = 0,
  liveUnreadBusCount = 0, hasMissingRefuel = false,
}: {
  orgCode: string | null;
  zanaEnabled: boolean;
  onOpenAnalytics: () => void;
  onUpgrade?: () => void;
  onExecuteIntent?: ZanaIntentExecutor;
  liveUnreadAlertCount?: number;
  /** Buses carrying at least one unread alert, live. */
  liveUnreadBusCount?: number;
  /** Fuel Report card: active buses with no refill in 30d. */
  hasMissingRefuel?: boolean;
}) {
  const [brief, setBrief] = useState<Briefing | null>(null);
  const [loading, setLoading] = useState(true);
  const [busyConfiguring, setBusyConfiguring] = useState(false);
  const [configError, setConfigError] = useState<string | null>(null);

  useEffect(() => {
    if (!orgCode || !zanaEnabled) { setLoading(false); return; }
    let alive = true;
    setLoading(true);
    // Backend read (rules block the browser's aiBriefings read for some admins).
    fetchZanaBriefing(orgCode)
      .then((b) => { if (alive) { setBrief(b as Briefing | null); setLoading(false); } })
      .catch(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [orgCode, zanaEnabled]);

  // ── Basic / Pilot: compact upsell strip ──────────────────────────────────
  if (!zanaEnabled) {
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 16, borderRadius: 16, padding: '14px 18px', color: '#fff', background: HERO_BG, overflow: 'hidden' }}>
        <Zana state="sleeping" size={56} animated />
        <div style={{ flex: '1 1 260px', minWidth: 0 }}>
          <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.08em', color: '#c4b3ff', textTransform: 'uppercase' }}>Zana Mode · Premium</div>
          <div style={{ fontSize: 15, fontWeight: 800, marginTop: 2 }}>Wake Zana for a weekly fleet-health briefing, right here.</div>
        </div>
        <button onClick={() => onUpgrade?.()} style={{ flexShrink: 0, padding: '10px 18px', borderRadius: 11, border: 'none', cursor: 'pointer', background: 'linear-gradient(135deg,#9b7cff,#6f4ff0)', color: '#fff', fontSize: 13, fontWeight: 800, boxShadow: '0 10px 24px -10px rgba(111,79,240,.8)' }}>
          Upgrade to wake Zana →
        </button>
      </div>
    );
  }

  // ── Premium / Enterprise ─────────────────────────────────────────────────
  // "Still learning" and "no briefing has ever been written" are different
  // states and must not share a UI: the first has real progress to report, the
  // second has none. Conflating them is what produced a permanent 5% bar.
  const notFiledYet = !brief && !loading;
  const learning = brief?.source === 'learning';
  const configuring = brief?.source === 'configuring';
  const stats = brief?.learnedStats || {};
  const gaps = brief?.learningGaps || [];
  const knownPct = Math.round((brief?.learningProgress ?? 0) * 100);
  const band = healthBand(brief?.healthScore);
  // Only flagged when the refresh has actually stopped (a whole cycle missed),
  // so the everyday "written this morning" case stays quiet.
  const age = (learning || configuring) ? null : briefingAge(brief);
  const staleAge = age?.stale ? age : null;
  // Urgent backend faces (theft / savings) win; otherwise the band drives her
  // look so "steadiness going down" is visible without a number.
  const urgentFace = brief?.mascotState === 'alert' || brief?.mascotState === 'eureka'
    ? brief.mascotState
    : null;
  const face: ZanaState = loading ? 'scanning'
    : configuring ? 'scanning'
      : (learning || notFiledYet) ? 'thinking'
        : (urgentFace || band?.face || brief?.mascotState || 'happy');

  async function handleConfigure() {
    if (!orgCode || busyConfiguring) return;
    setBusyConfiguring(true);
    setConfigError(null);
    try {
      const result = await runZanaFleetRead(orgCode, {
        mode: 'configure',
        onBrief: (b) => setBrief(b as Briefing | null),
      });
      if (!result.ok) {
        setConfigError(result.message || 'Could not finish reading the fleet. Please try again.');
      }
    } finally {
      setBusyConfiguring(false);
    }
  }
  /* Briefings are cached (they cost a model call); unread alerts are live
     mutable state. Two different staleness problems, both handled here:

     1. Cached line says "alerts", live queue is empty → drop it and fall
        through to the next recommendation.
     2. Cached line says "3 unread on 1 bus", live is 5 on 2 → the PROSE is
        still right, only its numbers have rotted. Rewrite the counts from
        live state rather than regenerating the briefing: a count must never
        be wrong, but paying for a model call every time an alert lands is not
        the way to keep it right.

     Anything without a live counterpart (fuel, routes, drivers) is shown as
     written — that analysis does not go stale in minutes. */
  const isAlertLine = (s: string) => /\b(unread|alert|high[- ]?risk|mark .*reviewed)\b/i.test(s || '');
  const recommendations = asList<{ text?: string }>(brief?.recommendations);
  const rawTopAction = recommendations.find((r) => (
    liveUnreadAlertCount > 0 || !isAlertLine(r.text || '')
  ))?.text;
  const topAction = (rawTopAction && isAlertLine(rawTopAction) && liveUnreadAlertCount > 0)
    ? `${liveUnreadAlertCount} unread alert${liveUnreadAlertCount === 1 ? '' : 's'}`
      + (liveUnreadBusCount > 0
        ? ` on ${liveUnreadBusCount} bus${liveUnreadBusCount === 1 ? '' : 'es'}`
        : '')
      + ' — Open High Risk Alerts and mark them reviewed once handled.'
    : rawTopAction;
  const bannerIntents = intentsFromInsightText(topAction, {
    liveUnreadCount: liveUnreadAlertCount,
    includeZanaMode: true,
    hasMissingRefuel,
  });

  return (
    <div data-zt="summaryCard" style={{ position: 'relative', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 20, borderRadius: 16, padding: '18px 22px', color: '#fff', background: HERO_BG, overflow: 'hidden' }}>
      <div style={{ position: 'absolute', top: -70, right: -40, width: 240, height: 240, borderRadius: '50%', background: 'radial-gradient(circle, rgba(111,79,240,.45), transparent 70%)', pointerEvents: 'none' }} />
      <div style={{ position: 'relative', flexShrink: 0 }}>
        <Zana state={face} size={88} ground animated />
      </div>

      <div style={{ position: 'relative', flex: '1 1 320px', minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9, fontSize: 12, fontWeight: 700, color: '#c4b3ff' }}>
          <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: '.1em', color: '#241a3a', background: '#c4b3ff', padding: '2px 7px', borderRadius: 5 }}>ZANA</span>
          Fleet Health
        </div>

        {loading ? (
          <div style={{ fontSize: 18, fontWeight: 800, marginTop: 8 }}>Waking Zana…</div>
        ) : configuring ? (
          /* The staged configure run: each line of progress here is a Firestore
             read that has actually completed — the backend checkpoints the doc
             after every stage, so this bar cannot lie. */
          <>
            <div style={{ fontSize: 18, fontWeight: 800, marginTop: 6 }}>
              {brief?.configureStage || 'Reading your fleet…'}
            </div>
            <div style={{ marginTop: 12, maxWidth: 460 }}>
              <div style={{ height: 6, borderRadius: 999, background: 'rgba(255,255,255,.14)', overflow: 'hidden' }}>
                <div style={{
                  height: '100%',
                  width: `${Math.round(((brief?.configureStageIndex ?? 1) / (brief?.configureTotalStages ?? 4)) * 100)}%`,
                  borderRadius: 999, background: 'linear-gradient(90deg,#9b7cff,#c4b3ff)', transition: 'width .6s ease',
                }} />
              </div>
              <div style={{ fontSize: 12, color: '#a99fce', marginTop: 6 }}>
                Step {brief?.configureStageIndex ?? 1} of {brief?.configureTotalStages ?? 4}
                {stats.buses ? ` · ${stats.buses} vehicles · ${stats.drivers ?? 0} drivers · ${stats.children ?? 0} children found` : ''}
              </div>
            </div>
          </>
        ) : notFiledYet ? (
          /* No briefing document exists at all. We genuinely know nothing about
             how far along she is, so show NO progress bar — an animated bar at
             5% with "0 vehicles" was pure theatre, and it was the thing that
             made Zana look like she wasn't reading anything. */
          <>
            <div style={{ fontSize: 18, fontWeight: 800, marginTop: 6 }}>Zana hasn't read your fleet yet.</div>
            <div style={{ fontSize: 13.5, color: '#c9c0e6', marginTop: 4, lineHeight: 1.5 }}>
              One tap and she'll index it now — buses, drivers, routes, fuel — and file her first briefing.
            </div>
            {isBriefingsConfigured() && (
              <button
                onClick={handleConfigure}
                disabled={busyConfiguring}
                style={{
                  marginTop: 12, padding: '9px 16px', borderRadius: 10, border: 'none',
                  cursor: busyConfiguring ? 'wait' : 'pointer', fontSize: 13, fontWeight: 800, color: '#241a3a',
                  background: 'linear-gradient(90deg,#9b7cff,#c4b3ff)', opacity: busyConfiguring ? 0.7 : 1,
                }}
              >
                {busyConfiguring ? 'Reading your fleet…' : '⚙ Configure Zana now'}
              </button>
            )}
            {configError && (
              <div style={{ fontSize: 12.5, color: '#fca5a5', marginTop: 8, lineHeight: 1.4 }}>{configError}</div>
            )}
          </>
        ) : learning ? (
          <>
            <div style={{ fontSize: 18, fontWeight: 800, marginTop: 6 }}>
              Zana knows your fleet {knownPct}% so far.
            </div>
            <div style={{ fontSize: 13.5, color: '#c9c0e6', marginTop: 4, lineHeight: 1.5 }}>
              {/* State what she ALREADY knows first — she does know the fleet from
                  day one; what she lacks is having watched it run. */}
              {stats.buses
                ? `I've got your ${stats.buses} vehicle(s)${stats.drivers ? `, ${stats.drivers} driver(s)` : ''}${stats.children ? ` and ${stats.children} child(ren)` : ''} on file. `
                : "I don't see any vehicles on your account yet. "}
              {gaps.length ? `Still learning: ${gaps.slice(0, 2).join('; ')}.` : 'Building out the rest now.'}
            </div>
            <div style={{ marginTop: 12, maxWidth: 460 }}>
              <div style={{ height: 6, borderRadius: 999, background: 'rgba(255,255,255,.14)', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${Math.max(2, knownPct)}%`, borderRadius: 999, background: 'linear-gradient(90deg,#9b7cff,#c4b3ff)', transition: 'width .8s ease' }} />
              </div>
              <div style={{ fontSize: 12, color: '#a99fce', marginTop: 6 }}>
                {/* Real counts, and the telemetry fraction stated as a fraction of
                    the real fleet rather than a bare "0 vehicles". */}
                {stats.buses ?? 0} vehicles · watched {stats.busesWithTelemetry ?? 0}/{stats.buses ?? 0} run
                {' · '}{stats.learnedRoutes ?? 0}/{stats.routes ?? 0} routes learned · {stats.trips ?? 0} trips read
              </div>
            </div>
            {asList<any>(brief?.missions).slice(0, 2).length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
                {asList<any>(brief?.missions).slice(0, 2).map((m) => (
                  <div key={m.id} style={{ background: 'rgba(255,255,255,.06)', border: '1px solid rgba(196,179,255,.25)', borderRadius: 10, padding: '8px 12px' }}>
                    <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '.06em', color: '#c4b3ff', textTransform: 'uppercase' }}>{m.pillar}</div>
                    <div style={{ fontSize: 13, fontWeight: 800, marginTop: 2 }}>{m.title}</div>
                    <div style={{ fontSize: 12, color: '#c9c0e6', marginTop: 2, lineHeight: 1.4 }}>{m.nextStep}</div>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginTop: 6, flexWrap: 'wrap' }}>
              {band && (
                <span style={{
                  fontSize: 28, fontWeight: 800, lineHeight: 1,
                  color: band.label === 'Clean' ? '#b8f0c8'
                    : band.label === 'Steady' ? '#e7dfff'
                      : '#fca5a5',
                }}>
                  {band.label}
                </span>
              )}
              <span style={{ fontSize: 16, fontWeight: 800, lineHeight: 1.25 }}>{brief?.headline || 'Your fleet at a glance'}</span>
              {staleAge && (
                <span
                  title={`Briefing written ${new Date(staleAge.atMs).toLocaleString()} — Zana's daily refresh has not run since`}
                  style={{
                    fontSize: 11, fontWeight: 800, color: '#241a3a', background: '#fbbf24',
                    padding: '2px 8px', borderRadius: 999, whiteSpace: 'nowrap',
                  }}
                >
                  ⚠ Out of date · {staleAge.label}
                </span>
              )}
            </div>
            {topAction && (
              <div style={{ fontSize: 13.5, color: '#c9c0e6', marginTop: 6, lineHeight: 1.5 }}>
                <b style={{ color: '#e7dfff' }}>Do next —</b> {topAction}
              </div>
            )}
            {onExecuteIntent && bannerIntents.length > 0 && (
              <ZanaActionChips
                intents={bannerIntents}
                onExecuteIntent={onExecuteIntent}
                tone="dark"
                onResult={(line) => customToast.show(line, 'success')}
              />
            )}
          </>
        )}
      </div>

      <button onClick={() => {
        if (onExecuteIntent) {
          void onExecuteIntent({ actionId: 'open_zana_mode', params: {} });
        } else {
          onOpenAnalytics();
        }
      }} style={{ position: 'relative', flexShrink: 0, padding: '10px 18px', borderRadius: 11, border: '1px solid rgba(196,179,255,.4)', cursor: 'pointer', background: 'rgba(255,255,255,.08)', color: '#fff', fontSize: 13, fontWeight: 800, whiteSpace: 'nowrap' }}>
        See full analysis →
      </button>
    </div>
  );
}
